import { map, atom } from 'nanostores';
import { upgradesData } from '../../generated/upgrades';
import { profileStore, profileActions } from '../../stores/game/profile';
import { getConstants } from './ConstantsDB';

/**
 * Типы статистик для вычисляемых показателей
 */
export interface GameStats {
  // Mining stats (только вычисляемые параметры)
  laser_flat_bonus: number; // Бонус к плоскому урону кирки (из апгрейдов)
  laser_dmg_mult: number; // Мультипликатор урона кирки (из апгрейдов)
  mine_radius_mult: number; // Мультипликатор радиуса добычи (из апгрейдов)
  crit_chance: number; // Шанс критического удара (из апгрейдов)
  crit_mult_bonus: number; // Бонус к мультипликатору крита (из апгрейдов)
  supercrit_chance: number; // Шанс супер-крита (из апгрейдов)
  supercrit_mult_bonus: number; // Бонус к мультипликатору супер-крита (из апгрейдов)
  ultracrit_chance: number; // Шанс ультра-крита (из апгрейдов)
  attack_speed: number; // Скорость атаки (из апгрейдов)
  laser_dmg_per_card: number; // Урон за карту (из апгрейдов)

  // Crafting stats
  craft_double_chance: number; // Шанс двойного крафта
  craft_triple_chance: number; // Шанс тройного крафта
  craft_speed_mult: number; // Скорость крафта

  // Resource stats
  triple_rock_chance: number; // Шанс тройного камня/руды
  ore_sell_mult: number; // Мультипликатор цены продажи руды

  // Bomb stats
  bomb_dmg_mult: number; // Мультипликатор урона бомб
  bomb_radius_mult: number; // Мультипликатор радиуса бомб

  // Energy stats
  energy_max: number; // Максимальная энергия
  energy_regen_mult: number; // Скорость регенерации энергии

  // General stats
  coin_mult: number; // Мультипликатор монет
  exp_mult: number; // Мультипликатор опыта

  // Advanced stats
  upgrade_coin_cost_mult: number; // Мультипликатор стоимости апгрейдов (скидка)
  enemy_armor_reduction: number; // Снижение брони врагов
  meter_fill_rate_mult: number; // Мультипликатор скорости заполнения шкалы
  upg_cap_plus: number; // Дополнительные капы апгрейдов
  bomb_recharge_rate: number; // Скорость перезарядки бомб
  free_bomb_chance: number; // Шанс бесплатной бомбы
  bomb_crit_mult_bonus: number; // Бонус к критическому урону бомб
  bomb_ultracrit_chance: number; // Шанс ультра-крита бомб
}

/**
 * Empty stats for safe initialization
 */
const emptyStats: GameStats = {
  laser_flat_bonus: 0,
  laser_dmg_mult: 1,
  mine_radius_mult: 1,
  crit_chance: 0,
  crit_mult_bonus: 0,
  supercrit_chance: 0,
  supercrit_mult_bonus: 0,
  ultracrit_chance: 0,
  attack_speed: 1,
  laser_dmg_per_card: 0,
  craft_double_chance: 0,
  craft_triple_chance: 0,
  craft_speed_mult: 1,
  triple_rock_chance: 0,
  ore_sell_mult: 1,
  bomb_dmg_mult: 1,
  bomb_radius_mult: 1,
  energy_max: 100,
  energy_regen_mult: 1,
  coin_mult: 1,
  exp_mult: 1,
  upgrade_coin_cost_mult: 0,
  enemy_armor_reduction: 0,
  meter_fill_rate_mult: 1,
  upg_cap_plus: 0,
  bomb_recharge_rate: 1,
  free_bomb_chance: 0,
  bomb_crit_mult_bonus: 0,
  bomb_ultracrit_chance: 0,
};

/**
 * Парсер формул для effect_per_lvl
 */
class FormulaParser {
  /**
   * Вычислить значение по формуле
   */
  static evaluate(formula: string | number, level: number): number {
    if (typeof formula === 'number') {
      return formula;
    }

    if (typeof formula !== 'string') {
      return 0;
    }

    try {
      // Заменяем переменные в формуле
      let expression = formula
        .replace(/i/g, level.toString()) // i = уровень апгрейда
        .replace(/flat=/g, '') // убираем префикс flat=
        .replace(/mult=/g, '') // убираем префикс mult=
        .replace(/^=/, ''); // убираем префикс =

      // Безопасное вычисление выражения
      return this.safeEval(expression);
    } catch (error) {
      console.warn(`Failed to evaluate formula: ${formula}`, error);
      return 0;
    }
  }

  /**
   * Безопасное вычисление математических выражений
   */
  private static safeEval(expression: string): number {
    // Разрешенные функции и операторы
    const allowedChars = /^[0-9+\-*/().\s]+$/;
    if (!allowedChars.test(expression)) {
      throw new Error(`Invalid characters in expression: ${expression}`);
    }

    // Используем Function constructor для безопасного eval
    try {
      return new Function(`return ${expression}`)();
    } catch (error) {
      throw new Error(`Failed to evaluate: ${expression}`);
    }
  }
}

/**
 * База данных для вычисляемых статистик
 * Реактивно обновляется при изменении апгрейдов
 */
export class StatsDB {
  private statsStore = map<GameStats>(emptyStats);
  private upgradeLevelsStore = atom<Record<string, number>>({});
  private isInitialized = false;

  constructor() {
    // Конструктор должен быть пустым для ленивой инициализации
  }

  /**
   * Инициализация, вызывается один раз загрузчиком игры
   */
  init(): void {
    if (this.isInitialized) return;

    // Подписываемся на изменения профиля для получения уровней апгрейдов
    profileStore.subscribe(profile => {
      const upgradeLevels: Record<string, number> = { ...profile.upgrades };
      Object.keys(upgradesData).forEach(upgradeId => {
        if (!(upgradeId in upgradeLevels)) {
          upgradeLevels[upgradeId] = 0;
        }
      });
      this.upgradeLevelsStore.set(upgradeLevels);
    });

    // Подписываемся на изменения уровней апгрейдов для пересчета статистик
    this.upgradeLevelsStore.subscribe(upgradeLevels => {
      this.recalculateStats(upgradeLevels);
    });

    // Первоначальный пересчет
    this.recalculateStats(this.upgradeLevelsStore.get());

    this.isInitialized = true;
  }

  /**
   * Возвращает базовые значения статистик, используя Constants
   */
  private getBaseStats(): GameStats {
    const constants = getConstants();
    return {
      ...emptyStats, // Start with defaults
      crit_chance: constants.getOr('K_CRIT_CHANCE_BASE', 0.05),
      ore_sell_mult: constants.getOr('K_SELL_MULT', 1),
      bomb_radius_mult: constants.getOr('K_BOMB_AOE_BASE', 1) / 2.2, // Нормализуем к 1
    };
  }

  /**
   * Маппинг ключей из upgrades.ts в ключи GameStats
   */
  private mapStatKey(upgradeStatKey: string): keyof GameStats | null {
    const keyMap: Record<string, keyof GameStats> = {
      laser_flat_base: 'laser_flat_bonus',
      laser_mult_base: 'laser_dmg_mult',
      mine_radius_mult: 'mine_radius_mult',
      crit_chance: 'crit_chance',
      crit_mult_bonus: 'crit_mult_bonus',
      craft_double_chance: 'craft_double_chance',
      craft_speed_mult: 'craft_speed_mult',
      triple_rock_chance: 'triple_rock_chance',
      ore_sell_mult: 'ore_sell_mult',
      bomb_dmg_mult: 'bomb_dmg_mult',
      bomb_radius_mult: 'bomb_radius_mult',
      energy_max: 'energy_max',
      energy_regen_mult: 'energy_regen_mult',
      coin_mult: 'coin_mult',
      exp_mult: 'exp_mult',
      attack_speed: 'attack_speed',
      supercrit_chance: 'supercrit_chance',
      supercrit_mult_bonus: 'supercrit_mult_bonus',
      ultracrit_chance: 'ultracrit_chance',
      bomb_ultracrit_chance: 'bomb_ultracrit_chance',
      bomb_crit_mult_bonus: 'bomb_crit_mult_bonus',
      craft_triple_chance: 'craft_triple_chance',
      laser_dmg_per_card: 'laser_dmg_per_card',
      upgrade_coin_cost_mult: 'upgrade_coin_cost_mult',
      enemy_armor_reduction: 'enemy_armor_reduction',
      meter_fill_rate_mult: 'meter_fill_rate_mult',
      upg_cap_plus: 'upg_cap_plus',
      bomb_recharge_rate: 'bomb_recharge_rate',
      free_bomb_chance: 'free_bomb_chance',
    };

    return keyMap[upgradeStatKey] || null;
  }

  /**
   * Пересчет всех статистик на основе уровней апгрейдов
   */
  private recalculateStats(upgradeLevels: Record<string, number>): void {
    const newStats = this.getBaseStats();

    // Проходим по всем апгрейдам и применяем их эффекты
    Object.entries(upgradesData).forEach(([upgradeId, upgradeConfig]) => {
      const level = upgradeLevels[upgradeId] || 0;
      if (level <= 0) return;

      const mappedStatKey = this.mapStatKey(upgradeConfig.effect_type_stat_key);
      if (!mappedStatKey) {
        console.warn(
          `Unknown stat key: ${upgradeConfig.effect_type_stat_key} for upgrade ${upgradeId}`
        );
        return;
      }

      const effectValue = FormulaParser.evaluate(upgradeConfig.effect_per_lvl, level);

      // Применяем эффект в зависимости от типа
      if (upgradeConfig.effect_per_lvl.toString().startsWith('flat=')) {
        // Для laser_flat_base - заменяем значение (полное значение на уровне)
        // Для остальных flat эффектов - добавляем
        if (mappedStatKey === 'laser_flat_bonus') {
          (newStats[mappedStatKey] as number) = effectValue;
        } else {
          (newStats[mappedStatKey] as number) += effectValue;
        }
      } else if (mappedStatKey.includes('_mult') || mappedStatKey.includes('_bonus')) {
        // Мультипликативные эффекты и бонусы - аддитивно к базовому значению
        (newStats[mappedStatKey] as number) += effectValue;
      } else {
        // По умолчанию считаем аддитивным
        (newStats[mappedStatKey] as number) += effectValue;
      }
    });

    // Применяем капы и ограничения
    this.applyCaps(newStats);

    this.statsStore.set(newStats);
  }

  /**
   * Применение капов и ограничений к статистикам
   */
  private applyCaps(stats: GameStats): void {
    const constants = getConstants();
    // Критический шанс не может быть больше максимального значения из констант
    const maxCritChance = constants.getOr('K_CRIT_CAP', 0.75);
    stats.crit_chance = Math.min(stats.crit_chance, maxCritChance);

    // Шансы не могут быть отрицательными
    stats.crit_chance = Math.max(stats.crit_chance, 0);
    stats.craft_double_chance = Math.max(stats.craft_double_chance, 0);
    stats.craft_triple_chance = Math.max(stats.craft_triple_chance, 0);
    stats.triple_rock_chance = Math.max(stats.triple_rock_chance, 0);

    // Мультипликаторы не могут быть меньше 0.1 (хардкод, пока нет в таблице)
    Object.keys(stats).forEach(key => {
      const value = stats[key as keyof GameStats];
      if (typeof value === 'number' && key.includes('mult') && value < 0.1) {
        (stats as any)[key] = 0.1;
      }
    });
  }

  /**
   * Получить текущие статистики
   */
  getStats(): Readonly<GameStats> {
    if (!this.isInitialized) {
      console.warn('StatsDB not initialized, returning empty stats.');
      return this.statsStore.get();
    }
    return this.statsStore.get();
  }

  /**
   * Подписаться на изменения статистик
   */
  subscribeStats(callback: (stats: GameStats) => void): () => void {
    if (!this.isInitialized) {
      console.warn('StatsDB not initialized, subscription might not work as expected.');
    }
    return this.statsStore.subscribe(callback);
  }

  /**
   * Получить конкретную статистику
   */
  getStat<K extends keyof GameStats>(key: K): GameStats[K] {
    if (!this.isInitialized) {
      console.warn(`StatsDB not initialized, returning default for ${key}.`);
    }
    return this.statsStore.get()[key];
  }

  /**
   * Получить статистику с fallback
   */
  getStatOr<K extends keyof GameStats>(key: K, fallback: GameStats[K]): GameStats[K] {
    if (!this.isInitialized) {
      return fallback;
    }
    const stats = this.statsStore.get();
    return stats[key] ?? fallback;
  }

  /**
   * Принудительно обновить статистики
   */
  refresh(): void {
    if (!this.isInitialized) return;
    const upgradeLevels = this.upgradeLevelsStore.get();
    this.recalculateStats(upgradeLevels);
  }

  /**
   * Установить уровень апгрейда (синхронизирует с профилем)
   */
  setUpgradeLevel(upgradeId: string, level: number): void {
    if (!this.isInitialized) return;
    // Обновляем через профиль, чтобы изменения сохранялись
    // profileActions уже импортирован в начале файла
    profileActions.setUpgradeLevel(upgradeId, level);
  }

  /**
   * Получить уровень апгрейда
   */
  getUpgradeLevel(upgradeId: string): number {
    if (!this.isInitialized) return 0;
    return this.upgradeLevelsStore.get()[upgradeId] || 0;
  }

  /**
   * Получить все уровни апгрейдов
   */
  getAllUpgradeLevels(): Record<string, number> {
    if (!this.isInitialized) return {};
    return { ...this.upgradeLevelsStore.get() };
  }
}

// Ленивый синглтон для удобства
let _statsInstance: StatsDB | null = null;
function getStatsDB(): StatsDB {
  if (!_statsInstance) {
    _statsInstance = new StatsDB();
  }
  return _statsInstance;
}

// Экспортируем функцию для получения экземпляра (как в других DB)
export function getStats(): StatsDB {
  return getStatsDB();
}

export const Stats = new Proxy({} as StatsDB, {
  get(_target, prop) {
    return getStatsDB()[prop as keyof StatsDB];
  },
});
